/**
 * E2e bootstrap (APP-002 §34): browser-side MSW + Clerk test-mode harness.
 *
 * Activated ONLY when the page loads with `?e2e=1`. In that mode the app:
 *   1. Starts a browser-side MSW worker that intercepts the dashboard backend
 *      (no real API calls, no production credentials).
 *   2. Overrides the Clerk auth boundary so the app sees a signed-in session
 *      with a mocked token, WITHOUT weakening any production component — the
 *      override is installed here, at the bootstrap boundary, before the app
 *      mounts, and only when the flag is present.
 *
 * Default CI requires no production Clerk credentials. The production build
 * (no flag) is completely unaffected. This closes the APP-001 follow-up for
 * authenticated-shell e2e.
 *
 * The handlers come from e2e/fixtures.ts so the mocked data is shared between
 * the harness and any per-test overrides.
 */
import type { SessionTokenProvider } from '@/api/client';

const E2E_FLAG = 'e2e';

function isE2eMode(): boolean {
  try {
    return new URLSearchParams(window.location.search).has(E2E_FLAG);
  } catch {
    return false;
  }
}

/**
 * Install the e2e harness if the flag is present. Returns a promise the app
 * can await before mounting so the MSW worker is ready (no unintercepted
 * requests leak to the network).
 *
 * SECURITY (APP-010 §29–30, §54): the entire harness — MSW worker, mock
 * fixtures, Clerk token override, and the `__E2E_AUTH__` window flag — is
 * hard-gated on `import.meta.env.MODE === 'test'`. Vite replaces that
 * expression with a string literal at build time, so in the production build
 * the guard is statically `'production' === 'test'` (i.e. `false`), the early
 * return is unconditional, and Rollup dead-code-eliminates every dynamic
 * `import()` below. The production artifact therefore contains no MSW, no
 * fixtures chunk, no token override, and no `?e2e=1` activation path. The
 * harness lives only in the `--mode test` e2e build.
 */
export async function installE2eHarness(): Promise<void> {
  if (import.meta.env.MODE !== 'test') return;
  if (!isE2eMode()) return;

  // 1. Browser-side MSW worker. The worker script (mockServiceWorker.js) is
  //    registered from the public dir.
  const { setupWorker } = await import('msw/browser');
  const msw = await import('msw');
  const { e2eHandlers } = await import('../../e2e/fixtures');
  const worker = setupWorker(...e2eHandlers);
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: `${import.meta.env.BASE_URL}mockServiceWorker.js` },
  });

  // Expose a per-test billing-state override on window. Tests cannot import
  // `msw` inside page.evaluate (no module resolver), so this helper closes over
  // the already-bundled msw + worker to register a one-off GET
  // /v1/dashboard/billing handler (APP-006 §44 needs no-subscription,
  // past-due/grace, and annual states). Gated behind the e2e flag; the worker
  // never starts in the production build.
  (window as unknown as {
    __e2eOverrideBilling?: (summary: Record<string, unknown>) => void;
  }).__e2eOverrideBilling = (summary: Record<string, unknown>) => {
    worker.use(
      msw.http.get('http://localhost:8080/v1/dashboard/billing', () =>
        msw.HttpResponse.json(summary, {
          status: 200,
          headers: {
            'X-Request-ID': 'req_e2e_billing_override',
            'Cache-Control': 'private, no-store',
          },
        }),
      ),
    );
  };

  // 2. Override the Clerk auth boundary. We install a module-level override
  //    that the ClerkProvider reads when the flag is present. This does NOT
  //    modify any production component — it is read-only at the boundary.
  const tokenProvider: SessionTokenProvider = async () => 'e2e-clerk-jwt';
  const { configureSessionTokenProvider } = await import('@/api/client');
  configureSessionTokenProvider(tokenProvider);

  // Publish a window flag the Clerk e2e shim (e2e/clerk-e2e.ts) reads.
  (window as unknown as { __E2E_AUTH__?: unknown }).__E2E_AUTH__ = {
    isLoaded: true,
    isSignedIn: true,
    userId: 'user_e2e_001',
    token: 'e2e-clerk-jwt',
  };
}
